import type { TReviewLineItem } from "./createViewFileReview";
import type { TSearchableOption } from "@/ui/molecules/SearchableSelect";

/** Subset of line item field metadata used for client-side validation. */
export type TLineItemFieldDefForValidation = {
    key: keyof TReviewLineItem;
    label: string;
    required?: boolean;
    alwaysReadOnly?: boolean;
    readOnlyWhenHasValue?: boolean;
};

export type TValidateLineEditFormParams = {
    form: Partial<TReviewLineItem>;
    fields: TLineItemFieldDefForValidation[];
};

/**
 * Client-side validation for the file review line edit modal.
 * Mirrors a subset of backend rules (required mapped account/propcode).
 */
export function validateLineEditForm(
    params: TValidateLineEditFormParams,
): Partial<Record<keyof TReviewLineItem, string>> {
    const errors: Partial<Record<keyof TReviewLineItem, string>> = {};
    const { form, fields } = params;

    for (const field of fields) {
        if (!field.required || field.alwaysReadOnly) continue;
        const val = form[field.key];
        if (val === null || val === undefined || String(val).trim() === "") {
            errors[field.key] = `${field.label} is required`;
        }
    }

    // Note: the mapped entity/asset/investment fields are selection-only via SearchableSelect; no "valid code" list
    // check is needed here — only the required check above applies.

    return errors;
}

/**
 * Split an optional `CODE: message` wrapper. Stored validation errors carry the
 * prefix; the review wire strips it before lines reach the editor, so callers see
 * either form and the router below must work on both.
 */
function splitBackendLineMessage(raw: string): { code: string | null; message: string } {
    const t = raw.trim();
    const codeMatch = t.match(/^([A-Z][A-Z0-9_]+):\s*(.+)$/s);
    if (codeMatch) return { code: codeMatch[1]!, message: codeMatch[2]!.trim() };
    return { code: null, message: t };
}

/**
 * Validation codes the APAC backend emits for the three mapped identifiers
 * (`_missing_mapping_errors` and the validator's `_INACTIVE_FLAGS`), and the edit
 * field each one belongs to.
 */
const IDENTIFIER_CODE_FIELDS: Record<string, keyof TReviewLineItem> = {
    MISSING_PROPERTY_CODE: "propcode",
    INACTIVE_PROPERTY_CODE: "propcode",
    MISSING_ASSET_CODE: "mappedAsset",
    INACTIVE_ASSET_CODE: "mappedAsset",
    MISSING_INVESTMENT_CODE: "mappedInvestment",
    INACTIVE_INVESTMENT_CODE: "mappedInvestment",
};

/**
 * The same routing for messages that have lost their code prefix:
 * "Entity is required", "Asset is not mapped", "Investment is inactive in master
 * data: X", and so on.
 */
const IDENTIFIER_MESSAGE_FIELDS: Array<[RegExp, keyof TReviewLineItem]> = [
    [/^entity is (required|not mapped|inactive)/i, "propcode"],
    [/^asset is (required|not mapped|inactive)/i, "mappedAsset"],
    [/^investment is (required|not mapped|inactive)/i, "mappedInvestment"],
];

/**
 * Maps backend line validation messages to edit-modal field keys so invalid rows
 * highlight the same areas as the table tooltip. Covers `FileValidationService` and
 * OTB row validators.
 */
export function mapBackendLineItemValidationErrorsToFields(
    validationErrors: string[],
): Partial<Record<keyof TReviewLineItem, string>> {
    const out: Partial<Record<keyof TReviewLineItem, string>> = {};

    const setMsg = (key: keyof TReviewLineItem, msg: string) => {
        const prev = out[key];
        out[key] = prev && prev !== msg ? `${prev}; ${msg}` : msg;
    };

    for (const raw of validationErrors) {
        const { code, message: msg } = splitBackendLineMessage(raw);
        if (!msg) continue;

        const identifierField =
            (code ? IDENTIFIER_CODE_FIELDS[code] : undefined) ??
            IDENTIFIER_MESSAGE_FIELDS.find(([pattern]) => pattern.test(msg))?.[1];
        if (identifierField) {
            setMsg(identifierField, msg);
            continue;
        }

        if (
            /row must have debit\/credit or amount value/i.test(msg) ||
            /^AMOUNT is required/i.test(msg)
        ) {
            setMsg("amount", msg);
            continue;
        }

        if (/debit amount cannot be negative/i.test(msg)) {
            setMsg("debit", msg);
            continue;
        }

        if (/credit amount cannot be negative/i.test(msg)) {
            setMsg("credit", msg);
            continue;
        }

        if (/account number is required/i.test(msg)) {
            setMsg("breAccountNumber", msg);
            continue;
        }

        if (/SOURCE_ACCOUNT_NUMBER is required/i.test(msg)) {
            setMsg("sourceAccountNumber", msg);
            continue;
        }

        if (/LOCAL_PROPERTY_CODE is required/i.test(msg)) {
            setMsg("localPropcode", msg);
            continue;
        }

        if (/property code not mapped|unmapped property/i.test(msg)) {
            setMsg("propcode", msg);
            setMsg("localPropcode", msg);
        }
    }

    return out;
}

export function mergeLineEditFieldErrors(params: {
    server: Partial<Record<keyof TReviewLineItem, string>>;
    client: Partial<Record<keyof TReviewLineItem, string>>;
    showClientErrors: boolean;
    editingLineItem: TReviewLineItem | null;
    editFormData: Partial<TReviewLineItem>;
}): Partial<Record<keyof TReviewLineItem, string>> {
    const { server, client, showClientErrors, editingLineItem, editFormData } = params;
    const merged: Partial<Record<keyof TReviewLineItem, string>> = { ...server };

    if (editingLineItem) {
        for (const k of Object.keys(merged) as (keyof TReviewLineItem)[]) {
            const init = editingLineItem[k];
            const cur = editFormData[k];
            if (String(init ?? "") !== String(cur ?? "")) {
                delete merged[k];
            }
        }
    }

    if (showClientErrors) {
        for (const k of Object.keys(client) as (keyof TReviewLineItem)[]) {
            const v = client[k];
            if (v) merged[k] = v;
        }
    }

    return merged;
}

/**
 * Apply a regular (non-account) field change to the form.
 */
export function applyLineItemFieldChange(
    prev: Partial<TReviewLineItem>,
    key: keyof TReviewLineItem,
    value: string | number,
): Partial<TReviewLineItem> {
    return { ...prev, [key]: value };
}

/**
 * Apply an account selection (from either the code or name SearchableSelect) to the form.
 * Selecting from either field sets BOTH breAccountNumber and breAccountName.
 * Passing null clears both fields.
 */
export function applyAccountSelection(
    prev: Partial<TReviewLineItem>,
    option: TSearchableOption | null,
): Partial<TReviewLineItem> {
    if (option === null) {
        return { ...prev, breAccountNumber: null, breAccountName: null };
    }
    const code = String(option["code"] ?? option.value);
    const name = String(option["name"] ?? option.label);
    return { ...prev, breAccountNumber: code, breAccountName: name };
}
